/**
 * LÄGG UPP FIRMAN — vägen in för den som ingen har bjudit in.
 *
 * Utan den här skärmen finns bara en väg in i Klartid: att någon annan redan är
 * här och lägger upp dig. Den som laddar ner appen själv möter annars ett konto
 * utan firma och kommer aldrig längre (App.tsx, världen 'ingen_firma').
 *
 * SKÄRMEN STÅR PÅ TVÅ STÄLLEN, och det är därför den inte rör navigeringen
 * själv:
 *   1. I världen 'ingen_firma' — som med flit ligger UTANFÖR <Navigeringsvard>
 *      (se App.tsx). Där renderas den rakt in i <Yttreram>.
 *   2. Som registrerad skärm 'nyfirma' inne i appen, för den som REDAN är med i
 *      en firma och vill lägga upp sin egen. Det är inget kantfall: en inhyrd
 *      (beslut 17) är gäst hos någon annan och driver eget vid sidan av — för
 *      honom visas 'ingen_firma' aldrig.
 * Därför `useNavigeringOm()` och inte `useNavigering()`: den senare kastar
 * utanför sin värd, vilket är rätt för varje annan skärm men fel för den här.
 *
 * TVÅ ID:N MYNTAS HÄR, INTE PÅ SERVERN. CreateCompanyRequest bär både `id` och
 * `default_price_list_id`, och worker/src/util/ids.ts säger varför ordagrant:
 * "servern tilldelar ALDRIG ett id till något användaren skapat". Båda är ULID
 * ur api/id.ts — samma algoritm som servern kräver — och märks genom
 * api/granska.ts, klientens enda ställe med ett bart `as <id-märke>`.
 *
 * OP_ID MYNTAS EN GÅNG OCH ÄNDRAS ALDRIG (beslut 09), precis som i NyOrder. När
 * knappen trycks fryses en PLAN — op_id OCH hela kroppen — och fälten låses. Ett
 * omtag skickar SAMMA op_id med SAMMA kropp och spelas då upp ur
 * account_op_log i stället för att lägga upp en andra firma; en ÄNDRAD kropp med
 * samma op_id ger DUPLICATE_OP och ingenting händer (worker/src/idempotency/
 * op-log.ts). Därför släpps planen — och myntas ett nytt op_id — i exakt två
 * lägen: när servern SVARAT och avvisat (då finns ingen firma), och när
 * användaren själv väljer "Ändra uppgifterna". Efter ett NÄTFEL ligger planen
 * kvar, för då vet vi inte om anropet gick fram.
 *
 * LICENSBEVISET SPARAS INTE HÄR. CREATE_COMPANY svarar med { company, license },
 * och api/klient.ts lägger licensen i valvet automatiskt (#absorberaBevis) —
 * exakt samma väg som vid inloggningen. Skärmen behöver bara peka ut den nya
 * firman som den aktiva och gå till startsidan.
 */

import { useState } from 'react';
import { CREATE_COMPANY } from '@delat/routes';
import { PRICE_PER_USER_ORE } from '@delat/types';
import type { CreateCompanyRequest, OpId } from '@delat/types';
import { atgardFor, granska, klienten, nyUlid } from '../api';
import type { Klartidfel } from '../api';
import { MAX_FIRMANAMN_TECKEN } from '../api/konstanter';
import { granskaSkapadFirma } from '../api/granskare/konto';
import { visaOren } from '../app/datum';
import { useNavigeringOm } from '../app/navigering';
import { useSession } from '../app/session';
import { Falt, Felruta, Knapp, Tomtext, Yta } from '../ui';

interface Plan {
  readonly op_id: OpId;
  /** Kroppen, färdig och frusen. Ett omtag måste skicka BYTE för BYTE samma. */
  readonly begaran: CreateCompanyRequest;
}

export const NyFirma = () => {
  const { bytFirma } = useSession();
  const navigering = useNavigeringOm();

  const [namn, sattNamn] = useState('');
  const [orgnr, sattOrgnr] = useState('');

  const [plan, sattPlan] = useState<Plan | null>(null);
  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<Readonly<Record<string, string>>>({});

  const last = plan !== null;

  /**
   * Klientens egen förkontroll. Servern gör den riktiga (parseCreateCompany mot
   * schemats CHECK), men org.nr ska sägas ifrån om HÄR — regeln är densamma som
   * api/granska.ts orgnummer redan bär: tio siffror, bindestreck är det
   * användaren skriver.
   */
  const byggPlan = (): Plan | null => {
    const brister: Record<string, string> = {};

    const rensatNamn = namn.trim();
    if (rensatNamn === '') brister['name'] = 'Firman behöver ett namn.';
    else if (rensatNamn.length > MAX_FIRMANAMN_TECKEN) {
      brister['name'] = `Högst ${MAX_FIRMANAMN_TECKEN} tecken.`;
    }

    const siffror = orgnr.replace(/[\s-]/g, '');
    if (siffror === '') brister['org_number'] = 'Skriv firmans org.nr.';
    else if (!/^\d{10}$/.test(siffror)) brister['org_number'] = 'Org.nr är tio siffror.';

    if (Object.keys(brister).length > 0) {
      sattFaltfel(brister);
      return null;
    }
    sattFaltfel({});

    return {
      op_id: granska.opId(nyUlid(), 'op_id'),
      begaran: {
        id: granska.companyId(nyUlid(), 'company.id'),
        name: rensatNamn,
        org_number: granska.orgnummer(siffror, 'org_number'),
        // Listan läggs upp TOM av servern (CreateCompanyRequest): första
        // tidraden ger PRICE_MISSING tills chefen fyllt i den. Id:t är ändå
        // vårt — servern namnger ingenting användaren skapat.
        default_price_list_id: granska.priceListId(nyUlid(), 'default_price_list_id'),
      },
    };
  };

  /** Var felet ska synas: på ett fält, eller som ruta över formuläret. */
  const visaFel = (nytt: Klartidfel): void => {
    if (nytt.slag === 'api' && nytt.code === 'ORG_NUMBER_TAKEN') {
      // Kontraktets egen text säger vad man gör: be den som äger firman bjuda
      // in dig. Den visas ordagrant, och på det fält den handlar om.
      sattFaltfel({ org_number: nytt.text });
      sattPlan(null);
      return;
    }
    if (nytt.slag === 'api' && nytt.code === 'VALIDATION_FAILED') {
      const falt = nytt.detalj?.['field'];
      if (falt === 'name' || falt === 'org_number') {
        sattFaltfel({ [falt]: nytt.text });
        sattPlan(null);
        return;
      }
      // 'id' eller 'default_price_list_id' är vår egen myntning — inget
      // användaren kan rätta, och alltså en bugg som ska synas i loggen.
      console.error(`[klartid] CREATE_COMPANY avvisade fältet ${String(falt)}. Klientbugg.`);
    }
    sattFel(nytt);
    // SERVERN HAR SVARAT OCH SAGT NEJ -> ingen firma blev till, och fälten får
    // öppnas igen. Vid ett nätfel (retry) vet vi inte om anropet gick fram: då
    // ligger planen kvar, och nästa tryck bär samma op_id.
    if (nytt.slag === 'api' && atgardFor(nytt) === 'give_up') sattPlan(null);
  };

  const spara = (): void => {
    const aktuell = plan ?? byggPlan();
    if (aktuell === null) return;
    if (plan === null) sattPlan(aktuell);

    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await klienten.anrop(
        CREATE_COMPANY,
        { data: aktuell.begaran, op_id: aktuell.op_id },
        granskaSkapadFirma,
      );
      // Alltid av igen, oavsett utfall: en knapp som fastnar i "Lägger upp ..."
      // efter ett tappat nät är en app man startar om.
      sattSparar(false);
      if (!svar.ok) {
        visaFel(svar.fel);
        return;
      }

      // Skaparen är manager OCH ägare (beslut 14) — det står i svarets
      // CompanySummary och behöver inte gissas här. Licensen ligger redan i
      // valvet; kvar är att göra firman aktiv, vilket hämtar ME på nytt och
      // lyfter sessionen från 'ingen_firma' till 'inloggad'.
      bytFirma(svar.varde.company.id);
      // Startsidan. Innanför appen går det genom navigeringen (ersatt, så att
      // bakåtknappen inte studsar tillbaka hit); i världen 'ingen_firma' finns
      // ingen navigering än — där byts adressen direkt, och <Navigeringsvard>
      // läser den när den monteras.
      if (navigering === null) globalThis.history.replaceState(null, '', '/');
      else navigering.ersatt('hem');
    })();
  };

  return (
    <>
      <Yta rubrik="Din firma">
        <Falt
          etikett="Firmans namn"
          varde={namn}
          onAndra={sattNamn}
          disabled={last}
          autoComplete="organization"
          placeholder="Ekror VVS AB"
          hjalp="Namnet står på fakturaunderlaget och är det dina kollegor ser."
          {...(faltfel['name'] === undefined ? {} : { fel: faltfel['name'] })}
        />
        <Falt
          etikett="Organisationsnummer"
          varde={orgnr}
          onAndra={sattOrgnr}
          disabled={last}
          inputMode="numeric"
          autoComplete="off"
          placeholder="5565551234"
          hjalp="Tio siffror. Bindestreck går bra — vi tar bort det."
          {...(faltfel['org_number'] === undefined ? {} : { fel: faltfel['org_number'] })}
        />
      </Yta>

      {fel !== null && <Felruta fel={fel} />}

      <Knapp disabled={sparar} onClick={spara}>
        {sparar ? 'Lägger upp ...' : plan === null ? 'Lägg upp firman' : 'Försök igen'}
      </Knapp>

      {last && !sparar && (
        <div style={{ marginTop: '0.5rem' }}>
          <Knapp
            variant="stor-tyst"
            onClick={() => {
              sattPlan(null);
              sattFel(null);
            }}
          >
            Ändra uppgifterna
          </Knapp>
        </div>
      )}

      {/* BARA VID NÄTFEL. Det är det enda läget där vi inte vet om anropet gick
          fram, och alltså det enda där skillnaden mellan "Försök igen" och
          "Ändra uppgifterna" spelar roll. */}
      {last && !sparar && fel !== null && fel.slag === 'nat' && (
        <Tomtext>
          Kom inget svar? Tryck Försök igen — sparningen bär samma id, så firman
          kan inte bli två. Ändrar du uppgifterna i stället får den ett nytt id.
        </Tomtext>
      )}

      {/* ANKARET ÄR DAGEN + TRIAL_DAYS, INTE DAGEN DU TRYCKER (beslut 18):
          "Firman signar den 20:e -> 7 gratisdagar -> första dragningen den 27:e
          -> sedan den 27:e varje månad." Texten sa tidigare att signeringsdagen
          blev betalningsdag, vilket är sju dagar fel — och det är precis den
          siffran en firma planerar sin likviditet efter.
          PRISET LÄSES UR KONTRAKTET och skrivs aldrig för hand: PRICE_PER_USER_ORE
          är 11 900 ören, och ändras det ändras texten med. Talet gäller varje
          person som är UPPLAGD med en betald roll — inhyrda och bokförare kostar
          ingenting. */}
      <Tomtext>
        Den som lägger upp firman blir dess ägare, och provperioden är sju dagar från
        den dagen. Sju dagar senare dras första betalningen —{' '}
        {visaOren(PRICE_PER_USER_ORE)} per person och månad, ex moms — och det datumet
        blir firmans betalningsdag varje månad. Aldrig den 1:a.
      </Tomtext>
    </>
  );
};
